import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Shield } from "lucide-react";
import PasswordInput from "../components/PasswordInput";

/**
 * Discrete admin-only login page (not advertised in the public navigation).
 * Coachees and HR users are redirected to the standard /login page.
 */
export default function AdminLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.role === "admin") {
        if (user.must_change_password) nav("/changer-mot-de-passe");
        else nav("/admin");
      } else {
        // Non-admin sessions are redirected to the standard area.
        nav("/dashboard");
      }
    }
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        setErr("Cet accès est réservé aux administrateurs. Merci d'utiliser l'espace de connexion principal.");
        return;
      }
      if (u.must_change_password) nav("/changer-mot-de-passe");
      else nav("/admin");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy text-ivory px-6 py-16">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <Shield size={18} className="text-brand" />
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand">— Accès administrateur</span>
        </div>
        <h1 className="font-serif text-3xl md:text-4xl font-light leading-tight mb-3 text-center">Espace confidentiel administrateur</h1>
        <p className="text-ivory/70 text-sm text-center mb-10">Cet accès est strictement réservé à l'équipe Audace &amp; Perspectives.</p>

        <form onSubmit={submit} data-testid="admin-login-form" className="bg-white/5 border border-ivory/15 backdrop-blur p-8 space-y-7">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ivory/60 mb-2">Email</label>
            <input data-testid="admin-login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent border-b border-ivory/25 py-2 text-ivory focus:outline-none focus:border-brand" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ivory/60 mb-2">Mot de passe</label>
            <PasswordInput data-testid="admin-login-password" required variant="dark" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {err && <div data-testid="admin-login-error" className="text-sm text-red-300">{err}</div>}
          <button data-testid="admin-login-submit" disabled={loading} type="submit" className="w-full bg-brand hover:bg-brandDark disabled:opacity-60 text-white py-4 text-xs uppercase tracking-[0.18em] font-mono">
            {loading ? "..." : "Se connecter"}
          </button>
        </form>

        <p className="text-center mt-8 text-xs text-ivory/45">
          Vous êtes coaché·e ? <a href="/login" className="underline hover:text-brand">Espace confidentiel</a>
        </p>
      </div>
    </div>
  );
}
